import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BlogPost, categoryColors, fetchBlogBySlug, fetchBlogs } from "@/pages/blog/api";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { injectJsonLd } from "@/utils/seo";
import { absoluteUrl } from "@/constants/site";
import { BLOG_INDEX_SEO, SEO_BRAND } from "@/constants/seoConfig";
import type { SEOMeta } from "@/utils/seo";

function toIsoDate(raw?: string): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [related, setRelated] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const seoMeta: SEOMeta = useMemo(() => {
    const path = slug ? `/blog/${slug}` : "/blog";
    if (post) {
      const desc = post.excerpt.trim().slice(0, 158);
      const keywords = [SEO_BRAND, post.category, ...post.tags].filter(Boolean).join(", ").slice(0, 400);
      return {
        title: `${post.title} | ${SEO_BRAND} Blog`,
        description: desc || `Read this article on the ${SEO_BRAND} mental wellness blog.`,
        canonicalPath: path,
        keywords,
        ogImagePath: post.image,
        ogType: "article",
        article: {
          publishedTime: toIsoDate(post.createdAt || post.date),
          modifiedTime: toIsoDate(post.updatedAt),
          section: post.category,
          tags: post.tags,
        },
      };
    }
    if (error) {
      return {
        title: `Article unavailable | ${SEO_BRAND}`,
        description: "This article could not be loaded. Browse other mental wellness posts on the AvachatAI blog.",
        canonicalPath: path,
        robots: "noindex, follow",
      };
    }
    if (!loading && !post && slug) {
      return {
        title: `Article not found | ${SEO_BRAND}`,
        description: "This blog post does not exist or was removed. Explore emotional wellness articles on AvachatAI.",
        canonicalPath: path,
        robots: "noindex, follow",
      };
    }
    return {
      title: `Blog | ${SEO_BRAND}`,
      description: BLOG_INDEX_SEO.description,
      canonicalPath: path,
      keywords: BLOG_INDEX_SEO.keywords,
    };
  }, [post, error, loading, slug]);

  useSEO(seoMeta);

  useEffect(() => {
    if (!post) {
      document.getElementById("avachat-article-ld")?.remove();
      return;
    }
    const logoUrl = absoluteUrl("/site-media/brand-icon-512.png");
    const data = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: [absoluteUrl(post.image)],
      datePublished: toIsoDate(post.createdAt || post.date),
      dateModified: toIsoDate(post.updatedAt) || toIsoDate(post.createdAt || post.date),
      author: {
        "@type": "Person",
        name: post.author,
      },
      publisher: {
        "@type": "Organization",
        name: SEO_BRAND,
        logo: { "@type": "ImageObject", url: logoUrl },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": absoluteUrl(`/blog/${post.slug}`),
      },
    };
    return injectJsonLd("avachat-article-ld", data);
  }, [post]);

  useEffect(() => {
    const loadBlogDetail = async () => {
      if (!slug) return;
      setLoading(true);
      setError("");
      try {
        const blog = await fetchBlogBySlug(slug);
        if (!blog) {
          setPost(null);
          setRelated([]);
          return;
        }
        setPost(blog);

        const relatedBlogs = await fetchBlogs({ category: blog.category, page: 1, limit: 6 });
        const filteredRelated = relatedBlogs
          .filter(
            (item) =>
              item.slug !== blog.slug &&
              (item.category === blog.category || item.tags.some((tag) => blog.tags.includes(tag)))
          )
          .slice(0, 3);
        setRelated(filteredRelated);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unable to load this article.");
      } finally {
        setLoading(false);
      }
    };

    loadBlogDetail();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#faf8ff" }}>
          <div className="text-center">
            <i className="ri-loader-4-line text-5xl text-[#7C3AED] mb-4 block animate-spin"></i>
            <p className="text-[#7a6aaa]">Loading article...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#faf8ff" }}>
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-red-400 mb-4 block"></i>
            <h1 className="font-display text-2xl font-bold text-[#1a1040] mb-2">Unable to load article</h1>
            <p className="text-[#7a6aaa] mb-6">{error}</p>
            <Link
              to="/blog"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-all"
              style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#faf8ff" }}>
          <div className="text-center">
            <i className="ri-article-line text-5xl text-[#c4b8f0] mb-4 block"></i>
            <h1 className="font-display text-2xl font-bold text-[#1a1040] mb-2">Article not found</h1>
            <p className="text-[#7a6aaa] mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/blog"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-all"
              style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const paragraphs = post.content.split("\n\n").filter((p) => p.trim());
  const publishedIso = toIsoDate(post.createdAt || post.date);

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <section className="pt-28 pb-10 px-4 md:px-8 relative overflow-hidden" style={{ background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #f8f5ff 100%)" }}>
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute w-96 h-96 rounded-full opacity-20 -top-20 -right-20" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)" }} />
            <div className="absolute w-64 h-64 rounded-full opacity-15 bottom-0 left-10" style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)" }} />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <nav aria-label="Breadcrumb" className="flex justify-center mb-4">
              <ol className="flex items-center gap-2 text-sm text-[#7a6aaa]" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/" className="hover:text-[#7C3AED] transition-colors" itemProp="item">
                    <span itemProp="name">Home</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <li>
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/blog" className="hover:text-[#7C3AED] transition-colors" itemProp="item">
                    <span itemProp="name">Blog</span>
                  </Link>
                  <meta itemProp="position" content="2" />
                </li>
                <li>
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <span className="text-[#7C3AED] font-medium" itemProp="name">
                    {post.title}
                  </span>
                  <meta itemProp="position" content="3" />
                </li>
              </ol>
            </nav>

            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-4"
              style={{ background: categoryColors[post.category] || "#7C3AED" }}
            >
              {post.category}
            </span>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1a1040] mb-4 leading-tight">{post.title}</h1>
            <div className="flex items-center justify-center gap-4 text-sm text-[#7a6aaa]">
              <span>{post.author}</span>
              <span>·</span>
              <time {...(publishedIso ? { dateTime: publishedIso } : {})}>{post.date}</time>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
          </div>
        </section>

        {/* Blend: gradient to white */}
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />

        <div className="max-w-3xl mx-auto px-4 md:px-8 -mt-6 relative z-10">
          <div className="rounded-2xl overflow-hidden border border-[#e8e0ff] shadow-sm">
            <img
              src={post.image}
              alt={post.title}
              title={`${post.title} - AvachatAI Blog`}
              className="w-full h-64 md:h-80 object-cover object-top"
              width={1200}
              height={630}
              loading="eager"
              decoding="async"
            />
          </div>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            {paragraphs.map((para, i) => {
              if (para.startsWith("## ")) {
                return (
                  <h2 key={i} className="font-display text-xl font-bold text-[#1a1040] mt-10 mb-4">
                    {para.replace("## ", "")}
                  </h2>
                );
              }
              if (para.startsWith("1. ") || para.startsWith("- ")) {
                const items = para.split("\n").filter((l) => l.trim());
                return (
                  <ul key={i} className="list-disc list-inside space-y-2 text-[#5a4a8a] mb-6 pl-2">
                    {items.map((item, j) => (
                      <li key={j} className="text-[#5a4a8a] leading-relaxed">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: item
                              .replace(/^\d+\.\s*/, "")
                              .replace(/^-\s*/, "")
                              .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"),
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={i} className="text-[#5a4a8a] leading-relaxed mb-6 text-base">
                  {para}
                </p>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-[#e8e0ff]">
            {post.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium text-[#7C3AED] bg-[#f5f0ff] border border-[#e0d8ff]">
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-6">
            <span className="text-sm text-[#7a6aaa]">Share:</span>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f0ff] text-[#7C3AED] hover:bg-[#ede8ff] transition-colors cursor-pointer"
              aria-label="Share on X"
            >
              <i className="ri-twitter-x-line text-sm"></i>
            </button>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f0ff] text-[#7C3AED] hover:bg-[#ede8ff] transition-colors cursor-pointer"
              aria-label="Share on Facebook"
            >
              <i className="ri-facebook-line text-sm"></i>
            </button>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f0ff] text-[#7C3AED] hover:bg-[#ede8ff] transition-colors cursor-pointer"
              aria-label="Share on LinkedIn"
            >
              <i className="ri-linkedin-line text-sm"></i>
            </button>
          </div>
        </article>

        {related.length > 0 && (
          <section className="max-w-3xl mx-auto px-4 md:px-8 pb-16">
            <h2 className="font-display text-lg font-bold text-[#1a1040] mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="rounded-xl overflow-hidden border border-[#e8e0ff] bg-white group cursor-pointer hover:-translate-y-1 transition-transform duration-300"
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      width={400}
                      height={200}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-4">
                    <span
                      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold text-white mb-2"
                      style={{ background: categoryColors[r.category] || "#7C3AED" }}
                    >
                      {r.category}
                    </span>
                    <h3 className="font-bold text-[#1a1040] text-sm leading-snug group-hover:text-[#7C3AED] transition-colors line-clamp-2">
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Blend: white to gradient */}
      <div className="h-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
      <Footer />
    </>
  );
}
